import { Alert, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import ActionButton from "components/Buttons/ActionButton";
import useNotifications from "features/Notifications/hooks/useNotifications";
import useSetPassword from "hooks/api/useSetPassword";
import useVidletNavigate from "hooks/navigation/useVidletNavigate";
import { usePersistStore } from "hooks/useStore";
import useValidateToken from "hooks/useValidateToken";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LOGIN_PAGE } from "utils/axiosInterceptors";
import hasLowercaseLetter from "utils/validators/hasLowercaseLetter";
import hasNumericCharacter from "utils/validators/hasNumericCharacter";
import hasSpecialCharacter from "utils/validators/hasSpecialCharacter";
import hasUppercaseLetter from "utils/validators/hasUppercaseLetter";
import PasswordInput from "./components/PasswordInput";
import PasswordRequirement from "./components/PasswordRequirement";

export default function NewPassword() {
  const { navigate } = useVidletNavigate();
  const { setNotification } = useNotifications();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") as string;
  const redirectUrl = searchParams.get("redirect_url");
  const [password, setPassword] = useState("");
  const [passwordVerify, setPasswordVerify] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const handlePSChangeAll = usePersistStore((store) => store.handlePSChangeAll);
  const { isTokenValid, isLoading, error } = useValidateToken(token);
  const {
    hasLowercase,
    hasMatchingPasswords,
    hasUppercase,
    hasSpecial,
    hasNumeric,
    isMinLength,
  } = useMemo(
    () => ({
      hasLowercase: hasLowercaseLetter(password),
      hasUppercase: hasUppercaseLetter(password),
      hasSpecial: hasSpecialCharacter(password),
      hasNumeric: hasNumericCharacter(password),
      hasMatchingPasswords: Boolean(password && password === passwordVerify),
      isMinLength: password.length >= 8,
    }),
    [password, passwordVerify],
  );

  const { setPassword: putPassword } = useSetPassword();

  async function onSetPassword() {
    setDisableSubmit(true);
    try {
      const response = await putPassword(password, token);
      handlePSChangeAll({ token: response.data.token });
      navigate(
        redirectUrl
          ? `${LOGIN_PAGE}?redirect_url=${encodeURIComponent(redirectUrl)}`
          : LOGIN_PAGE,
      );
      setNotification({
        message: "Password successfully changed! Please login.",
        autoHideDuration: undefined,
      });
    } catch (error) {
      setNotification({
        message:
          "There was a problem setting the password. Please try again later!",
        severity: "error",
      });
    }
  }

  if (!isLoading) {
    if (!token) return <Alert severity="error">Missing Token</Alert>;
    if (error)
      return (
        <Alert severity="error">{error?.response?.data.error?.message}</Alert>
      );
  }

  return (
    <Grid container flexDirection="column" spacing={1}>
      <Grid item xs={12}>
        {isTokenValid && (
          <form name="newPassword">
            <Typography>Choose a new password</Typography>
            <PasswordInput
              showPassword={showPassword}
              onShowChange={() => setShowPassword((value) => !value)}
              value={password}
              onChange={setPassword}
            />
            <Typography sx={{ mt: 2 }}>Re-enter password</Typography>
            <PasswordInput
              showPassword={showPasswordVerify}
              onShowChange={() => setShowPasswordVerify((value) => !value)}
              onChange={setPasswordVerify}
              value={passwordVerify}
            />
            <ul style={{ margin: "1rem" }}>
              <PasswordRequirement isValid={hasLowercase}>
                Password has a lowercase character
              </PasswordRequirement>
              <PasswordRequirement isValid={hasUppercase}>
                Password has an uppercase character
              </PasswordRequirement>
              <PasswordRequirement isValid={hasNumeric}>
                Password has a number
              </PasswordRequirement>
              <PasswordRequirement isValid={hasSpecial}>
                Password has one of these characters: ! @ # $ * ( )
              </PasswordRequirement>
              <PasswordRequirement isValid={isMinLength}>
                Password is at least 8 characters
              </PasswordRequirement>
              <PasswordRequirement isValid={hasMatchingPasswords}>
                Password entries match
              </PasswordRequirement>
            </ul>
            <ActionButton
              disabled={
                !password ||
                !hasLowercase ||
                !hasUppercase ||
                !hasNumeric ||
                !hasSpecial ||
                !isMinLength ||
                !hasMatchingPasswords ||
                disableSubmit
              }
              fullWidth
              onClick={onSetPassword}
            >
              Submit
            </ActionButton>
          </form>
        )}
      </Grid>
    </Grid>
  );
}
